import { create } from "zustand";
import {
  computeMissingSequences,
  getLastMsgSequence,
} from "../computeMissingSequences";
import { loadMailboxMessages, saveMailboxMessages } from "../storage";

export type MailboxSeverity = "default" | "warning" | "highPriority";

export interface MailboxMessage {
  messageId: string;
  accountKey: string;
  sequence: number;
  topicId: string;
  from: string;
  to: string;
  severity: MailboxSeverity;
  subject: string;
  body: string;
  sentAtUnix: number;
  isUserUnread: boolean;
  userReadAtUnix: number;
  isAdminUnread: boolean;
  adminReadAtUnix: number;
}

export interface VybeSyncMailboxRequest {
  isFirstLoad: boolean;
  lastMsgSequence: number;
  missingMsgSequences: number[];
}

interface MailboxStore {
  activeAccountKey: string | null;
  messages: MailboxMessage[];
  setActiveAccount: (accountKey: string | null) => void;
  addMessage: (message: MailboxMessage) => void;
  upsertMessage: (message: MailboxMessage) => void;
  applySyncBatch: (
    accountKey: string,
    batchMessages: MailboxMessage[],
    isLastBatch: boolean
  ) => void;
  buildSyncRequest: () => VybeSyncMailboxRequest;
  removeMessage: (messageId: string) => void;
  markUserRead: (messageId: string) => void;
  persistActiveAccount: () => void;
  clear: () => void;
}

function mapSeverity(value: unknown): MailboxSeverity {
  if (value === 1 || value === "Warning" || value === "warning") return "warning";
  if (value === 2 || value === "HighPriority" || value === "highPriority")
    return "highPriority";
  return "default";
}

function sortBySequenceDesc(messages: MailboxMessage[]): MailboxMessage[] {
  return [...messages].sort((a, b) => b.sequence - a.sequence);
}

function mergeMessages(
  existing: MailboxMessage[],
  incoming: MailboxMessage[]
): MailboxMessage[] {
  const bySequence = new Map<number, MailboxMessage>();
  for (const m of existing) bySequence.set(m.sequence, m);
  for (const m of incoming) bySequence.set(m.sequence, m);
  return sortBySequenceDesc(Array.from(bySequence.values()));
}

export function mapWireMailboxMessage(raw: Record<string, unknown>): MailboxMessage {
  return {
    messageId: String(raw.messageId ?? raw.MessageId ?? ""),
    accountKey: String(raw.accountKey ?? raw.AccountKey ?? ""),
    sequence: Number(raw.sequence ?? raw.Sequence ?? 0),
    topicId: String(raw.topicId ?? raw.TopicId ?? ""),
    from: String(raw.from ?? raw.From ?? ""),
    to: String(raw.to ?? raw.To ?? ""),
    severity: mapSeverity(raw.severity ?? raw.Severity),
    subject: String(raw.subject ?? raw.Subject ?? ""),
    body: String(raw.body ?? raw.Body ?? ""),
    sentAtUnix: Number(raw.sentAtUnix ?? raw.SentAtUnix ?? 0),
    isUserUnread: Boolean(raw.isUserUnread ?? raw.IsUserUnread ?? true),
    userReadAtUnix: Number(raw.userReadAtUnix ?? raw.UserReadAtUnix ?? 0),
    isAdminUnread: Boolean(raw.isAdminUnread ?? raw.IsAdminUnread ?? false),
    adminReadAtUnix: Number(raw.adminReadAtUnix ?? raw.AdminReadAtUnix ?? 0),
  };
}

export const useMailboxStore = create<MailboxStore>((set, get) => ({
  activeAccountKey: null,
  messages: [],

  setActiveAccount: (accountKey) => {
    if (!accountKey) {
      set({ activeAccountKey: null, messages: [] });
      return;
    }
    const messages = loadMailboxMessages(accountKey);
    set({ activeAccountKey: accountKey, messages });
  },

  addMessage: (message) => {
    const { activeAccountKey } = get();
    if (activeAccountKey && message.accountKey && message.accountKey !== activeAccountKey) {
      return;
    }
    set((state) => {
      const merged = mergeMessages(state.messages, [message]);
      if (activeAccountKey) saveMailboxMessages(activeAccountKey, merged);
      return { messages: merged };
    });
  },

  upsertMessage: (message) => get().addMessage(message),

  applySyncBatch: (accountKey, batchMessages, isLastBatch) => {
    const { activeAccountKey } = get();
    if (activeAccountKey !== accountKey) return;

    set((state) => {
      const merged = mergeMessages(state.messages, batchMessages);
      if (isLastBatch) saveMailboxMessages(accountKey, merged);
      return { messages: merged };
    });
  },

  buildSyncRequest: () => {
    const { messages } = get();
    const sequences = messages.map((m) => m.sequence).filter((s) => s > 0);
    const isFirstLoad = sequences.length === 0;
    return {
      isFirstLoad,
      lastMsgSequence: getLastMsgSequence(sequences),
      missingMsgSequences: isFirstLoad ? [] : computeMissingSequences(sequences),
    };
  },

  removeMessage: (messageId) =>
    set((state) => {
      const messages = state.messages.filter((m) => m.messageId !== messageId);
      if (state.activeAccountKey) saveMailboxMessages(state.activeAccountKey, messages);
      return { messages };
    }),

  markUserRead: (messageId) =>
    set((state) => {
      const messages = state.messages.map((m) =>
        m.messageId === messageId
          ? { ...m, isUserUnread: false, userReadAtUnix: Math.floor(Date.now() / 1000) }
          : m
      );
      if (state.activeAccountKey) saveMailboxMessages(state.activeAccountKey, messages);
      return { messages };
    }),

  persistActiveAccount: () => {
    const { activeAccountKey, messages } = get();
    if (activeAccountKey) saveMailboxMessages(activeAccountKey, messages);
  },

  clear: () => set({ activeAccountKey: null, messages: [] }),
}));
